/**
 * Utilidades para manejo consistente de coordenadas
 * Estándar adoptado:
 * - Base de datos: POINT(longitud latitud) 
 * - GeoJSON: [longitud, latitud]
 * - Usuario: "latitud, longitud"
 * - Google Maps: {lat: latitud, lng: longitud}
 */

/**
 * Convierte GeoJSON a formato para Google Maps
 * @param {Object} geoJson - Objeto GeoJSON con coordinates [lng, lat]
 * @returns {Object} - Objeto {lat, lng} para Google Maps
 */
export const geoJsonToLatLng = (geoJson) => {
  if (!geoJson || !geoJson.coordinates || geoJson.coordinates.length !== 2) {
    return null;
  }
  return {
    lat: geoJson.coordinates[1], // latitud es el segundo elemento
    lng: geoJson.coordinates[0]  // longitud es el primero
  };
};

/**
 * Convierte coordenadas lat/lng a GeoJSON
 * @param {number} lat - Latitud
 * @param {number} lng - Longitud  
 * @returns {Object} - Objeto GeoJSON
 */
export const latLngToGeoJson = (lat, lng) => ({
  type: "Point",
  coordinates: [lng, lat] // GeoJSON: [longitud, latitud]
});

/**
 * Convierte string "lat, lng" a objeto {lat, lng}
 * @param {string} coordString - String con formato "latitud, longitud"
 * @returns {Object} - Objeto {lat, lng}
 */
export const stringToLatLng = (coordString) => {
  if (!coordString || typeof coordString !== 'string') {
    return null;
  }
  
  const coords = coordString.split(",").map(coord => parseFloat(coord.trim()));
  if (coords.length !== 2 || coords.some(isNaN)) {
    return null;
  }
  
  return { 
    lat: coords[0], 
    lng: coords[1] 
  };
};

/**
 * Convierte objeto {lat, lng} a string para mostrar al usuario
 * @param {number|Object} lat - Latitud o objeto {lat, lng}
 * @param {number} lng - Longitud (opcional si lat es objeto)
 * @returns {string} - String "latitud, longitud"
 */
export const latLngToString = (lat, lng) => {
  if (typeof lat === 'object' && lat.lat !== undefined && lat.lng !== undefined) {
    return `${lat.lat}, ${lat.lng}`;
  }
  if (typeof lat === 'number' && typeof lng === 'number') {
    return `${lat}, ${lng}`;
  }
  return "";
};

/**
 * Convierte string "lat, lng" a GeoJSON
 * @param {string} coordString - String con formato "latitud, longitud"
 * @returns {Object} - Objeto GeoJSON
 */
export const stringToGeoJson = (coordString) => {
  const latLng = stringToLatLng(coordString);
  if (!latLng) return null;
  
  return latLngToGeoJson(latLng.lat, latLng.lng);
};

/**
 * Convierte GeoJSON a string para mostrar al usuario
 * @param {Object} geoJson - Objeto GeoJSON
 * @returns {string} - String "latitud, longitud"
 */
export const geoJsonToString = (geoJson) => {
  const latLng = geoJsonToLatLng(geoJson);
  if (!latLng) return "";
  
  return latLngToString(latLng);
};

/**
 * Valida si las coordenadas están en rangos válidos
 * @param {number} lat - Latitud
 * @param {number} lng - Longitud
 * @returns {boolean} - True si son válidas
 */
export const validateCoordinates = (lat, lng) => {
  return (
    typeof lat === 'number' && 
    typeof lng === 'number' &&
    lat >= -90 && lat <= 90 &&
    lng >= -180 && lng <= 180 &&
    !isNaN(lat) && !isNaN(lng)
  );
};

/**
 * Valida si un objeto GeoJSON tiene coordenadas válidas
 * @param {Object} geoJson - Objeto GeoJSON
 * @returns {boolean} - True si es válido
 */
export const validateGeoJson = (geoJson) => {
  if (!geoJson || geoJson.type !== 'Point' || !geoJson.coordinates) {
    return false;
  }
  
  const [lng, lat] = geoJson.coordinates;
  return validateCoordinates(lat, lng);
};

// Ejemplos de referencia para Colombia
export const REFERENCE_COORDINATES = {
  BOGOTA: {
    lat: 4.5930553,
    lng: -74.0954728,
    string: "4.5930553, -74.0954728",
    geoJson: { type: "Point", coordinates: [-74.0954728, 4.5930553] }
  },
  CALI: {
    lat: 3.4247219771943,
    lng: -76.547769191335,
    string: "3.4247219771943, -76.547769191335",
    geoJson: { type: "Point", coordinates: [-76.547769191335, 3.4247219771943] }
  }
};

// Límites geográficos de Colombia (con margen de tolerancia)
export const COLOMBIA_BOUNDS = {
  minLat: -4.5,   // Sur de Amazonas
  maxLat: 13.5,   // Norte de La Guajira
  minLng: -82.0,  // Oeste de Providencia
  maxLng: -66.5   // Este de Vichada
};

/**
 * Valida si las coordenadas están dentro de Colombia
 * @param {number} lat - Latitud
 * @param {number} lng - Longitud
 * @returns {boolean} - True si están dentro de Colombia
 */
export const validateColombiaCoordinates = (lat, lng) => {
  if (!validateCoordinates(lat, lng)) return false;

  return (
    lat >= COLOMBIA_BOUNDS.minLat &&
    lat <= COLOMBIA_BOUNDS.maxLat &&
    lng >= COLOMBIA_BOUNDS.minLng &&
    lng <= COLOMBIA_BOUNDS.maxLng
  );
};

/**
 * Valida un string de coordenadas y retorna información detallada
 * @param {string} coordString - String con formato "latitud, longitud"
 * @returns {Object} - { valid: boolean, error: string|null, lat: number, lng: number }
 */
export const validateCoordinateString = (coordString) => {
  // Verificar que no esté vacío
  if (!coordString || typeof coordString !== 'string' || !coordString.trim()) {
    return { valid: false, error: "Las coordenadas son requeridas", lat: null, lng: null };
  }

  const trimmed = coordString.trim();

  // Verificar formato básico (debe contener una coma)
  if (!trimmed.includes(",")) {
    return {
      valid: false,
      error: "Formato inválido. Use: latitud, longitud (ej: 4.6097, -74.0817)",
      lat: null,
      lng: null
    };
  }

  // Separar y parsear coordenadas
  const parts = trimmed.split(",").map(p => p.trim());

  if (parts.length !== 2) {
    return {
      valid: false,
      error: "Debe ingresar exactamente dos valores separados por coma",
      lat: null,
      lng: null
    };
  }

  const lat = parseFloat(parts[0]);
  const lng = parseFloat(parts[1]);

  // Verificar que sean números válidos
  if (isNaN(lat) || isNaN(lng)) {
    return {
      valid: false,
      error: "Las coordenadas deben ser valores numéricos",
      lat: null,
      lng: null
    };
  }

  // Verificar rangos válidos globales
  if (lat < -90 || lat > 90) {
    return {
      valid: false,
      error: "La latitud debe estar entre -90 y 90",
      lat,
      lng
    };
  }

  if (lng < -180 || lng > 180) {
    return {
      valid: false,
      error: "La longitud debe estar entre -180 y 180",
      lat,
      lng
    };
  }

  // Verificar que estén dentro de Colombia
  if (!validateColombiaCoordinates(lat, lng)) {
    return {
      valid: false,
      error: "Las coordenadas deben estar dentro del territorio colombiano",
      lat,
      lng
    };
  }

  return { valid: true, error: null, lat, lng };
};

/**
 * Regla de validación para Form.Item de Ant Design
 * @param {boolean} required - Si el campo es requerido
 * @returns {Function} - Función validadora para rules
 */
export const coordinateValidationRule = (required = false) => ({
  validator(_, value) {
    // Si no es requerido y está vacío, es válido
    if (!required && (!value || (typeof value === 'string' && !value.trim()))) {
      return Promise.resolve();
    }

    // Si es requerido y está vacío, el required rule ya lo maneja
    if (!value || (typeof value === 'string' && !value.trim())) {
      return Promise.resolve();
    }

    const result = validateCoordinateString(value);

    if (!result.valid) {
      return Promise.reject(new Error(result.error));
    }

    return Promise.resolve();
  }
});